import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";

import { FLASHPAY_ROLES, hasAnyRole, hasRole } from "@/core/auth";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useDeleteProvider } from "@/features/ledger/api/use-delete-provider";
import { ProviderFormDialog } from "@/features/ledger/components/providers/provider-form-dialog";
import type { ProviderRow } from "@/features/ledger/components/providers/providers-columns";

/**
 * Menu d'actions de ligne des providers.
 * Édition (`PUT`) réservée à flashpay-superadmin / flashpay-admin ;
 * désactivation (`DELETE`) réservée à flashpay-superadmin (masquage).
 */
export function ProviderRowActions({
  provider,
  navigate: _navigate,
}: {
  provider: ProviderRow;
  navigate: NavigateFunction;
}) {
  const remove = useDeleteProvider();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = hasAnyRole([
    FLASHPAY_ROLES.SUPERADMIN,
    FLASHPAY_ROLES.ADMIN,
  ]);
  const canDelete = hasRole(FLASHPAY_ROLES.SUPERADMIN);

  return (
    <>
      <RowActionsMenu
        actions={[
          ...(canEdit
            ? [
                {
                  id: "edit",
                  label: t("ledger.providers.actions.edit"),
                  icon: Pencil,
                  onClick: () => setEditOpen(true),
                },
              ]
            : []),
          ...(canDelete
            ? [
                {
                  id: "delete",
                  label: t("ledger.providers.actions.delete"),
                  icon: Trash2,
                  variant: "destructive" as const,
                  onClick: () => setDeleteOpen(true),
                },
              ]
            : []),
        ]}
      />

      {canEdit ? (
        <ProviderFormDialog
          provider={provider}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      ) : null}

      <MutationConfirmPreview
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        data={{
          id: provider.id,
          code: provider.code,
          name: provider.name,
          type: provider.type,
        }}
        title={t("common.mutationPreview.deleteTitle")}
        description={t("common.mutationPreview.deleteDescription")}
        confirmLabel={t("ledger.providers.actions.delete")}
        t={t}
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(provider.id, {
            onSuccess: () => {
              notifSilence(t("ledger.providers.feedback.deleted"));
              setDeleteOpen(false);
            },
            onError: (error) => notifSilenceError(error.message),
          })
        }
      />
    </>
  );
}
